import { MagicBackground, PersistentStore } from "@/stores/persistent_store";
import { computed, inject, Ref, ref, watch } from "vue";
import { useEditorStore, ViewMode } from "@/stores/editor_store";
import { useMonitorSize } from "@/composables/monitor_size";
import Client from "@/modules/internal_api/client";
import { ProcessingState, Image, ResultVariant, ProcessingError } from "@/modules/internal_api/image";
import { usePoll } from "./poll";
import { rbgMbGenerationClickedV100, rbgEditorNavigationClickedV100 } from "kaleido-event-tracker";

import { I18n } from "i18n-js";

export enum WidgetState {
  Ready = "ready",
  Loading = "loading",
  Error = "error",
  Generated = "generated",
}

type WidgetIcon = "generate" | "generating" | "generated";
interface WidgetProps {
  variant: "primary" | "overlay";
  copy: string;
  badgeAvailable?: boolean;
  icon?: WidgetIcon;
  action?: () => Promise<void> | void;
}

export function useMagicBackground(persistentStore: Ref<PersistentStore>) {
  const I18n: I18n = inject("I18n");

  const { poll } = usePoll();
  const error = ref<ProcessingError | Error | null>(null);

  const handleProcessingError = (processingError: ProcessingError) => {
    error.value = processingError;
  };

  const isLoading = computed(() => {
    if (!editorStore.selectedImage?.magicBackgroundResults) {
      return false;
    }
    return Array.from(editorStore.selectedImage.magicBackgroundResults.values()).some(
      (result) => result.magicBackgroundPreviewResult.state === ProcessingState.Processing
    );
  });

  const generatedBackgrounds = computed(() => {
    const currentImage = editorStore.selectedImage;
    if (!currentImage) {
      return [];
    }
    return parseBackgrounds(currentImage);
  });

  const selectedTemplate = computed(() => {
    return persistentStore.value.selectedMagicBackgroundTemplate;
  });

  const hasError = computed(() => !!error.value);
  const selectedMagicBackground = computed(() => {
    return persistentStore.value.selectedMagicBackground;
  });

  const hasSelectedMagicBackground = computed(() => {
    return persistentStore.value.selectedMagicBackground !== null;
  });

  const hasSelectedMagicBackgroundTemplate = computed(() => {
    return selectedTemplate.value !== null;
  });

  const isGenerated = computed(
    () =>
      hasSelectedMagicBackground.value &&
      persistentStore.value.selectedMagicBackground.state === ProcessingState.Finished
  );

  const currentState = computed(() => {
    if (error.value) return WidgetState.Error;
    if (isLoading.value || editorStore.isGeneratingBackground) return WidgetState.Loading;
    if (isGenerated.value) return WidgetState.Generated;
    if (persistentStore.value.selectedMagicBackground === null && selectedTemplate.value !== null)
      return WidgetState.Ready;
    if (persistentStore.value.selectedMagicBackground !== null && editorStore.openedPanel !== "addBackground")
      return WidgetState.Generated;
    return WidgetState.Ready;
  });

  const widgetBehaviorMap: Record<WidgetState, WidgetProps> = {
    [WidgetState.Ready]: {
      variant: "primary",
      copy: I18n.t("editor.magic_background.generate"),
      icon: "generate",
      badgeAvailable: true,
      action: async () => await generateMagicBackground(selectedTemplate.value.id),
    },
    [WidgetState.Loading]: {
      variant: "primary",
      copy: "",
      icon: "generating",
      badgeAvailable: false,
    },
    [WidgetState.Error]: {
      variant: "overlay",
      copy: I18n.t("editor.magic_background.generate_generic_error"),
      badgeAvailable: true,
    },
    [WidgetState.Generated]: {
      variant: "primary",
      copy: I18n.t("editor.magic_background.generate_again"),
      icon: "generate",
      badgeAvailable: true,
      action: async () => await regenerateMagicBackground(),
    },
  };

  const currentWidgetConfig = computed((): WidgetProps => {
    return widgetBehaviorMap[currentState.value];
  });

  const { isMobile, isMobileLarge } = useMonitorSize();
  const editorStore = useEditorStore();

  watch(
    () => editorStore.openedPanel,
    () => {
      if (
        (editorStore.openedPanel === "fx" || editorStore.openedPanel === "eraseRestore") &&
        persistentStore.value.selectedMagicBackground !== null
      ) {
        persistentStore.value.selectMagicBackground(null);
      }
    }
  );

  const enqueueMagicBackground = async (templateId: string, iterationId?: string) => {
    const image = editorStore.selectedImage;
    editorStore.isGeneratingBackground = true;
    try {
      let updatedImage = await Client.enqueueMagicBackground(image, templateId, iterationId);
      editorStore.updateImage(updatedImage);

      updatedImage = await poll(updatedImage, ResultVariant.MagicBackgroundPreview, handleProcessingError);

      if (updatedImage.magicBackgroundResults) {
        const generation = Array.from(updatedImage.magicBackgroundResults.entries()).pop()?.[1];
        if (generation.magicBackgroundPreviewResult.state === ProcessingState.Finished) {
          const parsedBackgrounds = parseBackgrounds(updatedImage);

          if (
            parsedBackgrounds.length > 0 &&
            parsedBackgrounds[parsedBackgrounds.length - 1].state === ProcessingState.Finished
          ) {
            persistentStore.value.setSelectedMagicBackgroundTemplate(null);
            persistentStore.value.selectMagicBackground(parsedBackgrounds[parsedBackgrounds.length - 1]);
          }
        } else if (generation.magicBackgroundPreviewResult.state === ProcessingState.Error) {
          error.value = generation.magicBackgroundPreviewResult as ProcessingError;
        }

        editorStore.isGeneratingBackground = false;
      }
    } catch (error) {
      handleProcessingError({
        message: `${error.message}`,
        code: "GENERATION_ERROR",
        state: ProcessingState.Error,
      });
    } finally {
      editorStore.isGeneratingBackground = false;
    }
  };

  const generateMagicBackground = async (templateId: string) => {
    const cache = persistentStore.value.magicBrushCache;
    const iterationId = cache?.iterationId;

    rbgMbGenerationClickedV100({ image_id: editorStore.selectedImage.meta.id, template_id: templateId });
    await enqueueMagicBackground(templateId, iterationId);
  };

  const regenerateMagicBackground = async () => {
    const templateId = persistentStore.value.selectedMagicBackground?.templateId;
    const iterationId = persistentStore.value.selectedMagicBackground?.iterationId;

    console.log("regenerateMagicBackground", templateId, iterationId);
    console.log("selectedMagicBackground", persistentStore.value.selectedMagicBackground);

    rbgMbGenerationClickedV100({ image_id: editorStore.selectedImage.meta.id, template_id: templateId });
    await enqueueMagicBackground(templateId, iterationId);
  };

  const resetError = () => {
    if (error.value) {
      error.value = null;
    }
  };

  const isGenerateMode = computed(() => {
    const shouldShowGenerateMode =
      (hasSelectedMagicBackgroundTemplate.value || persistentStore.value.selectedMagicBackground !== null) &&
      editorStore.openedPanel !== "eraseRestore" &&
      editorStore.openedPanel !== "fx";
    if (isMobile.value || isMobileLarge.value) {
      return shouldShowGenerateMode && editorStore.openedPanel === "addBackground";
    }
    return shouldShowGenerateMode;
  });

  const parseBackgrounds = (image: Image): MagicBackground[] => {
    const backgrounds = image.magicBackgroundResults;
    const parsedBackgrounds = [];

    backgrounds?.forEach((result, id) => {
      if ("url" in result.magicBackgroundPreviewResult) {
        parsedBackgrounds.push({
          id,
          state: result.magicBackgroundPreviewResult.state,
          url: result.magicBackgroundPreviewResult.url,
          templateId: result.magicBackgroundPreviewResult.templateId,
          iterationId: result.magicBackgroundPreviewResult.iterationId,
        });
      }
    });

    return parsedBackgrounds;
  };

  const shouldShowIntroWidget = computed(() => {
    return (
      editorStore.selectedImage?.previewResult.state === ProcessingState.Finished &&
      persistentStore.value.selectedMagicBackground === null &&
      persistentStore.value.selectedMagicBackgroundTemplate === null &&
      editorStore.openedPanel === null
    );
  });

  const shouldShowSelectInspirationWidget = computed(() => {
    return (
      editorStore.openedPanel === "addBackground" &&
      persistentStore.value.shouldShowSelectInspirationWidget &&
      selectedTemplate.value === null
    );
  });

  const hideSelectInspirationWidget = () => {
    persistentStore.value.withSnapshot(() => {
      persistentStore.value.disableSelectInspirationWidget();
    });
  };

  const generatedBackgroundCount = computed(() => {
    return generatedBackgrounds.value.length;
  });

  // ===== MB controls =====
  const isNavigationEnabled = computed(() => {
    return (
      (generatedBackgroundCount.value > 0 || editorStore.isGeneratingBackground) &&
      (editorStore.openedPanel === "addBackground" || editorStore.openedPanel === null) &&
      editorStore.viewMode !== ViewMode.Before &&
      !persistentStore.value.canZoomOut
    );
  });

  const hasNoMagicBackgroundsSelected = computed(() => {
    return persistentStore.value.selectedMagicBackground === null && generatedBackgroundCount.value > 0;
  });

  const isFirstBackground = computed(() => {
    return (
      generatedBackgrounds.value.findIndex(
        (background) => background.id === persistentStore.value.selectedMagicBackground?.id
      ) === 0
    );
  });

  const isLastBackground = computed(() => {
    return (
      generatedBackgrounds.value.findIndex(
        (background) => background.id === persistentStore.value.selectedMagicBackground?.id
      ) ===
      generatedBackgroundCount.value - 1
    );
  });

  const nextBackground = () => {
    const currentIndex = generatedBackgrounds.value.findIndex(
      (background) => background.id === persistentStore.value.selectedMagicBackground?.id
    );

    if (currentIndex === generatedBackgroundCount.value - 1) return;
    if (hasNoMagicBackgroundsSelected.value) {
      trackNavigation("generation");
      persistentStore.value.withSnapshot(() => {
        const firstBackground = generatedBackgrounds.value[0];
        persistentStore.value.selectMagicBackground(firstBackground);
      });
      return;
    }

    trackNavigation("generation");
    const nextIndex = (currentIndex + 1) % generatedBackgroundCount.value;
    persistentStore.value.selectMagicBackground(generatedBackgrounds.value[nextIndex]);
  };

  const previousBackground = () => {
    const currentIndex = generatedBackgrounds.value.findIndex(
      (background) => background.id === persistentStore.value.selectedMagicBackground?.id
    );
    if (currentIndex === -1) return;

    if (isFirstBackground.value) {
      persistentStore.value.withSnapshot(() => {
        persistentStore.value.selectMagicBackground(null);
      });
      trackNavigation("original");
      return;
    }
    const previousIndex = (currentIndex - 1 + generatedBackgroundCount.value) % generatedBackgroundCount.value;

    trackNavigation("generation");
    persistentStore.value.withSnapshot(() => {
      persistentStore.value.selectMagicBackground(generatedBackgrounds.value[previousIndex]);
    });
  };

  const trackNavigation = (selectedImageType: "generation" | "inspiration" | "original") => {
    rbgEditorNavigationClickedV100({
      image_id: editorStore.selectedImage.meta.id,
      image_type: selectedImageType,
    });
  };

  return {
    generatedBackgrounds,
    // States
    isLoading,
    hasError,
    isGenerated,
    isGenerateMode,
    error,
    currentWidgetConfig,
    currentState,
    shouldShowIntroWidget,
    shouldShowSelectInspirationWidget,
    selectedMagicBackground,
    selectedTemplate,
    // Actions
    enqueueMagicBackground,
    regenerateMagicBackground,
    resetError,
    parseBackgrounds,
    nextBackground,
    previousBackground,
    hideSelectInspirationWidget,
    // Navigation controls
    isNavigationEnabled,
    isFirstBackground,
    isLastBackground,
    hasNoMagicBackgroundsSelected,
  };
}
